import { DATE_TIME_FORMATTER } from "env.config";
import { getMES16ApiColor } from "services/developer";

const getFilteredApis = (allApis, currentHost) => {
  if (!allApis.length || !currentHost) {
    return [];
  }
  let helper = {};
  let filteredApis = [];
  const apis = currentHost.Apis;
  allApis.forEach((deployedApi) => {
    apis.forEach((assignedApi) => {
      if (deployedApi.swaggerApiName === assignedApi) {
        //  generic api priority display
        helper[assignedApi] = deployedApi;
      }
    });
  });
  // sort by name
  filteredApis = Object.values(helper).sort((first, second) => {
    if (first.swaggerApiName !== second.swaggerApiName) {
      return first.swaggerApiName.localeCompare(second.swaggerApiName);
    }
    return first;
  });
  return filteredApis;
};

const validateAppDetail = (appFormData) => {
  const { appName, oauthScope } = appFormData;
  if (
    oauthScope.length === 0 ||
    !appName ||
    !/^[0-9a-zA-Z_\s-]+$/.test(appName)
  ) {
    const newAppFormData = Object.assign({}, appFormData);
    if (oauthScope.length === 0) {
      newAppFormData.oauthScopeValid = false;
    }
    if (!appName) {
      newAppFormData.appNameValid = false;
      newAppFormData.appNameErrorMessage = "";
    } else {
      if (!/^[0-9a-zA-Z_\s-]+$/.test(appName)) {
        newAppFormData.appNameValid = false;
        newAppFormData.appNameErrorMessage =
          "App Name can only contain alphanumeric, space, dash, and underscore (for example, My Grade_1-0-0).";
      }
    }
    return newAppFormData;
  }
};

const validateHostDetail = (hostDetail) => {
  const { scope, stage, host, name } = hostDetail;
  /*   
    ^: Matches the beginning of the string.
    ([a-zA-Z0-9-]+\.){1,}: Matches one or more ( {1,} ) characters consisting of letters, numbers, or hyphens, followed by a dot ( . ). This part matches the subdomain.
    [a-zA-Z]{2,}: Matches at least two letters, representing the top-level domain (TLD) like com, net, org.
    $: Matches the end of the string.
    /i: Indicates a case-insensitive match. 
  */
  const reg = /^([a-zA-Z0-9-]+\.){1,}[a-zA-Z]{2,}$/i;
  const validHost = reg.test(host);
  if (!scope || !stage || !name || !host || !validHost) {
    const newHostDetail = {
      ...hostDetail,
      scopeValid: scope,
      stageValid: stage,
      hostValid: true,
      nameValid: name,
    };
    if (!host) {
      return { ...newHostDetail, hostValid: false };
    }
    if (!validHost) {
      return {
        ...newHostDetail,
        hostValid: "Please enter a valid host format.",
      };
    }
    return newHostDetail;
  }
};

const getMessageInfo = ({ success, operation, content }) => {
  const successMesMap = {
    addApp: "App was added successfully.",
    updateApp: "App was updated successfully.",
    deleteApp: "App was deleted successfully.",
    addProfile: "Profile was added successfully.",
    updateProfile: "Profile was updated successfully.",
    deleteProfile: "Profile was deleted successfully.",
    addHost: "Host was added successfully.",
    updateHost: "Host was updated successfully.",
    deleteHost: "Host was deleted successfully.",
    addAppHost: "Host was added successfully.",
    updateAppHost: "Host was updated successfully.",
    deleteAppHost: "Host was deleted successfully.",
    updateTarget: "Target was updated successfully.",
    addAppProfile: "Profile was added successfully.",
    deleteAppProfile: "Profile was deleted successfully.",
  };
  const failMesMap = {
    addApp: "Failed to add new App.",
    updateApp: "Failed to update App.",
    deleteApp: "Failed to delete App.",
    addProfile: "Failed to add new profile.",
    updateProfile: "Failed to update profile.",
    deleteProfile: "Failed to delete profile.",
    addHost: "Failed to add host.",
    updateHost: "Failed to update host.",
    deleteHost: "Failed to delete host.",
    addAppHost: "Failed to add host.",
    updateAppHost: "Failed to update host.",
    deleteAppHost: "Failed to delete host.",
    updateTarget: "Failed to update target.",
    addAppProfile: "Failed to add new profile.",
    deleteAppProfile: "Failed to delete profile.",
    addSubscriptions: "Failed to subscribe APIs.",
  };
  if (success) {
    return successMesMap[operation];
  }
  return content || failMesMap[operation];
};

const handleMessage = ({
  success,
  operation,
  setShowSuccessMes,
  setShowFailMes,
  content,
}) => {
  const message = getMessageInfo({ success, operation, content });
  if (success) {
    setShowFailMes(false);
    setShowSuccessMes(message);
  } else {
    setShowSuccessMes(false);
    setShowFailMes(message);
  }
};

const formatDate = (isoDateString) => {
  if (!isoDateString) isoDateString = new Date();
  return DATE_TIME_FORMATTER.format(new Date(isoDateString));
};

const resetSwaggerApiName = (MES, title, version, apiGatewayVersion, active) => {
  let resultTitle = title;
  if (MES === "MES15") {
    if (new RegExp(`.*-${active}$`).test(title)) {
      resultTitle = title.slice(0, title.lastIndexOf(active) - 1);
    }
  }
  if (MES === "MES16") {
    if (isMES16OauthAPI(title)) {
      resultTitle = title.slice(0, title.lastIndexOf(apiGatewayVersion?.oauthApi) - 1);
    }
    if (isMES16ErpAPI(title)) {
      resultTitle = title.slice(0, title.lastIndexOf(apiGatewayVersion?.erpApi) - 1);
    }
    if (new RegExp(`.*-${version}-${active}$`).test(title)) {
      resultTitle = title.slice(0, title.lastIndexOf(version) - 1);
    }
  }
  return resultTitle;
};

async function limitConcurrentRequests(getRequest, urls, maxConcurrency) {
  const results = {};
  async function processQueue() {
    while (urls.length > 0) {
      const url = urls.shift();
      const result = await getRequest(url);
      results[url] = result;
    }
  }
  const requestPromises = Array.from({ length: maxConcurrency }, processQueue);
  await Promise.all(requestPromises);
  return results;
}

const timeoutPromise = (ms, promise) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("error"));
    }, ms);
    promise.then(
      (result) => {
        clearTimeout(timeoutId);
        resolve(result);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
};

const getColorStatus = async (duration, url) => {
  return timeoutPromise(duration, getMES16ApiColor(url))
    .then((res) => {
      return (res && res.color) || "invalid";
    })
    .catch(() => {
      return "error";
    });
};

const isMES16OauthAPI = (apiName) => {
  return new RegExp(`^MES16-.*-oauth-api`).test(apiName);
};

const isMES16ErpAPI = (apiName) => {
  return new RegExp(`^MES16-.*-erp-api`).test(apiName);
};

export {
  getFilteredApis,
  validateAppDetail,
  getMessageInfo,
  handleMessage,
  formatDate,
  resetSwaggerApiName,
  limitConcurrentRequests,
  validateHostDetail,
  timeoutPromise,
  getColorStatus,
  isMES16OauthAPI,
  isMES16ErpAPI,
};
